import { fmtMoney, fmtHours, fmtNumber } from './format';
import { c, t } from '../../theme';

// ── The same month, last year, broken out by one dimension ───────────────────
//
// The table under the chart. The chart says the company moved; this says which
// job family, cost center, group or category moved it.
//
// Three things it refuses to do:
//
//   IT DOES NOT DROP A ROW THAT WENT TO ZERO. A job family that stopped
//   incurring overtime is the best news on the page, and a table built from
//   this year's rows alone cannot show it -- the row simply is not there, and
//   its absence reads as nothing rather than as a win. Rows come back from a
//   full outer join and a vanished one is labelled `gone`.
//
//   IT DOES NOT PRINT A PERCENTAGE AGAINST ZERO. A family with no overtime last
//   year and $40k this year is not "+100%", it is new. Growth from nothing has
//   no percentage, so the cell says `new` and the dollar column carries the
//   size.
//
//   IT DOES NOT LET A DELTA STAND UNQUALIFIED WHEN THE MONTHS ARE UNEQUAL. When
//   this month holds a different number of payroll runs than the same month
//   last year, the header says so in a sentence and the per-period column is
//   the one to read. The raw column stays -- it is the real money -- but it is
//   no longer the only thing on offer.

const STATUS_LABEL = {
  new:  'new',
  gone: 'gone',
  no_prior_data: '—',
};

function Delta({ pct, status }) {
  if (status === 'new')  return <span style={{ ...s.badge, ...s.badgeNew }}>new</span>;
  if (status === 'gone') return <span style={{ ...s.badge, ...s.badgeGone }}>gone</span>;
  if (pct === null || pct === undefined) return <span style={s.muted}>—</span>;

  // Up is not good and down is not bad in the abstract -- but on an overtime
  // board, less overtime is the goal, so the colours follow that reading.
  const tone = pct > 0 ? c.dangerText : pct < 0 ? c.successText : c.textMuted;
  const sign = pct > 0 ? '+' : '';
  return <span style={{ ...s.delta, color: tone }}>{sign}{pct.toFixed(1)}%</span>;
}

export default function YoyBreakdown({ data, isLoading, error, measure = 'amount' }) {
  if (isLoading) return <div style={s.card}><div style={s.empty}>Loading…</div></div>;
  if (error)     return <div style={s.card}><div style={s.empty}>Could not load the breakdown.</div></div>;
  if (!data || !data.rows) return null;

  const { rows, current, prior, comparability_note: note, totals, label } = data;
  const money = measure === 'amount';
  const val   = (r, k) => (money ? r[k === 'cur' ? 'ot_amount' : 'prior_ot_amount']
                                 : r[k === 'cur' ? 'ot_hours'  : 'prior_ot_hours']);
  const fmt   = money ? fmtMoney : fmtHours;
  const pctOf = (r) => (money ? r.delta_amount_pct : r.delta_hours_pct);

  // Per pay period, which is what survives an unequal month pair. Computed here
  // rather than asked of the API a second time: the API already returned both
  // period counts, and a second aggregate that can disagree with the first is a
  // bug waiting for a rounding difference.
  const perRun = (v, n) => (v === null || v === undefined || !n ? null : v / n);

  if (!rows.length) {
    return (
      <div style={s.card}>
        <div style={s.empty}>No overtime in {current?.label} or {prior?.label} for this cut.</div>
      </div>
    );
  }

  return (
    <div style={s.card}>
      <div style={s.head}>
        <div>
          <div style={s.title}>{label}</div>
          <div style={s.sub}>
            {current.label} <span style={s.runs}>({current.periods} run{current.periods === 1 ? '' : 's'})</span>
            {' vs '}
            {prior.label}{' '}
            <span style={s.runs}>
              {prior.loaded ? `(${prior.periods} run${prior.periods === 1 ? '' : 's'})` : '(not loaded)'}
            </span>
          </div>
        </div>
      </div>

      {note && <div style={s.warn}>{note}</div>}

      <div style={s.scroll}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, ...s.thLeft }}>{label}</th>
              <th style={s.th}>{current.label}</th>
              <th style={s.th}>{prior.label}</th>
              <th style={s.th}>Change</th>
              <th style={s.th} title="Per pay period — the figure that is comparable when the two months hold different numbers of payroll runs">
                Per run
              </th>
              <th style={s.th}>Employees</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const curRun   = perRun(val(r, 'cur'), current.periods);
              const priorRun = perRun(val(r, 'prior'), prior.periods);
              const runPct   = (curRun === null || priorRun === null || priorRun === 0)
                ? null : ((curRun - priorRun) / priorRun) * 100;
              return (
                <tr key={r.key} style={r.unmapped ? s.trUnmapped : undefined}>
                  <td style={{ ...s.td, ...s.tdLeft }}>
                    {/* Every dimension but Employee displays its own key, and
                        sends label: null. Employee is keyed on the payroll
                        number so a rename still pairs across the two years, and
                        carries the name to show instead. */}
                    {r.label || r.key}
                    {r.unmapped && <span style={s.unmappedTag}>not in the profile bible</span>}
                  </td>
                  <td style={s.td}>{fmt(val(r, 'cur'))}</td>
                  <td style={s.td}>
                    {r.prior_ot_amount === null ? <span style={s.muted}>—</span> : fmt(val(r, 'prior'))}
                  </td>
                  <td style={s.td}><Delta pct={pctOf(r)} status={r.status} /></td>
                  <td style={s.td}>
                    {runPct === null
                      ? <span style={s.muted}>{STATUS_LABEL[r.status] || '—'}</span>
                      : <Delta pct={runPct} status="both" />}
                  </td>
                  <td style={s.td}>
                    {fmtNumber(r.employee_count)}
                    {r.prior_employee_count !== null && (
                      <span style={s.was}> was {fmtNumber(r.prior_employee_count)}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {totals && (
            <tfoot>
              <tr>
                <td style={{ ...s.tf, ...s.tdLeft }}>Total</td>
                <td style={s.tf}>{fmt(money ? totals.ot_amount : totals.ot_hours)}</td>
                <td style={s.tf}>
                  {totals.prior_ot_amount === null
                    ? <span style={s.muted}>—</span>
                    : fmt(money ? totals.prior_ot_amount : totals.prior_ot_hours)}
                </td>
                <td style={s.tf} colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

const s = {
  card:  { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '10px', overflow: 'hidden' },
  empty: { padding: '40px 0', textAlign: 'center', color: c.textMuted, ...t.bodySm },

  head:  { padding: '13px 15px 11px', borderBottom: `1px solid ${c.border}` },
  title: { ...t.h3, color: c.text },
  sub:   { ...t.caption, color: c.textMuted, marginTop: '2px' },
  runs:  { color: c.textFaint },

  warn:  { ...t.caption, color: c.warningText, background: c.warningTint,
           borderBottom: `1px solid ${c.border}`, padding: '9px 15px' },

  scroll: { overflowX: 'auto' },
  table:  { width: '100%', borderCollapse: 'collapse' },

  th:     { ...t.label, color: c.textFaint, textAlign: 'right', padding: '9px 15px',
            borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' },
  thLeft: { textAlign: 'left' },

  td:     { ...t.bodySm, ...t.tabular, color: c.text, textAlign: 'right',
            padding: '9px 15px', borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' },
  tdLeft: { textAlign: 'left', fontVariantNumeric: 'normal' },

  tf:     { ...t.bodySm, ...t.tabular, color: c.textStrong, fontWeight: 600,
            textAlign: 'right', padding: '10px 15px', background: c.surfaceSunk },

  trUnmapped:  { background: c.surfaceSunk },
  unmappedTag: { ...t.micro, color: c.textFaint, marginLeft: '8px' },

  delta:  { fontWeight: 600 },
  muted:  { color: c.textFaint },
  was:    { ...t.micro, color: c.textFaint, marginLeft: '5px' },

  badge:     { ...t.micro, fontWeight: 700, padding: '2px 6px', borderRadius: '4px' },
  badgeNew:  { color: c.dangerText,  background: c.dangerTint },
  badgeGone: { color: c.successText, background: c.successTint },
};
